import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dropdown,
  Form,
  InputField,
  ConfirmationModal,
  StickySaveBar,
} from 'components';
import avatar from '../../images/avatar.png';
import { useDispatch, useSelector } from 'react-redux';
import {
  addUserDocument,
  fetchAvatar,
  removeUserDocument,
  AppDispatch,
  RootState,
} from 'data';
import { editProfile, fetchProfile } from 'data/slices/profileSlice';
import { useFormChanges } from 'hooks';
import { useTranslation } from 'react-i18next';
import {
  countries,
  DEFAULT_PHONE_COUNTRY,
  getApiErrorMessage,
  isValidPhoneNumber,
  languages,
  normalizePhoneNumber,
  splitPhoneNumber,
} from 'helpers';

interface ProfileSettingsFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneCountry: string;
  phone: string;
  newPassword: string;
  confirmPassword: string;
  profileLanguage: string;
  profileImage: string | File | null;
}

interface FormSaveHandler {
  save: () => Promise<void>;
  hasChanges: () => boolean;
}

export const ProfileSettings = ({ user, saveRef }: { user: any; saveRef?: React.MutableRefObject<FormSaveHandler | null> }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { loading } = useSelector((state: RootState) => state.profile);
  const { avatar: avatarDoc, loading: docLoading } = useSelector(
    (state: RootState) => state.document,
  );
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [phoneSaveError, setPhoneSaveError] = useState<string | null>(null);

  const [formData, setFormData] = useState<ProfileSettingsFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneCountry: DEFAULT_PHONE_COUNTRY,
    phone: '',
    newPassword: '',
    confirmPassword: '',
    profileLanguage: '',
    profileImage: null,
  });

  const [initialFormData, setInitialFormData] =
    useState<ProfileSettingsFormData>({
      firstName: '',
      lastName: '',
      email: '',
      phoneCountry: DEFAULT_PHONE_COUNTRY,
      phone: '',
      newPassword: '',
      confirmPassword: '',
      profileLanguage: '',
      profileImage: null,
    });

  useEffect(() => {
    dispatch(fetchAvatar());
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      const storedPhone = splitPhoneNumber(user.phone || '');
      const newData = {
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phoneCountry: storedPhone.country,
        phone: storedPhone.national,
        newPassword: '',
        confirmPassword: '',
        profileLanguage: user.language || '',
        profileImage: null,
      };
      setFormData(newData);
      setInitialFormData(newData);
    }
  }, [user]);

  const isFormChanged = useFormChanges(initialFormData, formData);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'phone') setPhoneSaveError(null);
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'phoneCountry') setPhoneSaveError(null);
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddPhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = e.target.files;
    if (files && files[0]) {
      try {
        if (avatarDoc?.id) {
          await dispatch(removeUserDocument({ id: avatarDoc.id })).unwrap();
        }

        // Set the new file in formData for preview
        setFormData((prev) => ({
          ...prev,
          profileImage: files[0],
        }));

        // Upload the new avatar
        const uploadResponse = await dispatch(
          addUserDocument({
            file: files[0],
            docType: 'avatar',
          }),
        ).unwrap();

        // Update formData with the new URL
        setFormData((prev) => ({
          ...prev,
          profileImage: uploadResponse.document.url,
        }));

        // Refresh avatar data
        await dispatch(fetchAvatar()).unwrap();
      } catch (error) {
        console.error('Error changing avatar:', error);
      }
    }
  };

  const handleRemovePhoto = () => {
    setIsRemoveModalOpen(true);
  };

  const confirmRemovePhoto = async () => {
    try {
      if (avatarDoc?.id) {
        await dispatch(removeUserDocument({ id: avatarDoc.id })).unwrap();
        setFormData((prev) => ({
          ...prev,
          profileImage: null,
        }));
        await dispatch(fetchAvatar()).unwrap();
      }
    } catch (error) {
      console.error('Error removing avatar:', error);
    }
    setIsRemoveModalOpen(false);
  };

  const phoneFormatInvalid =
    formData.phone.trim() !== '' &&
    !isValidPhoneNumber(formData.phoneCountry, formData.phone);

  const phoneError =
    phoneSaveError ??
    (submitted && phoneFormatInvalid
      ? (t('profileSettings.phoneInvalid') as string)
      : undefined);

  const handleSave = async () => {
	setSubmitted(true);
	setPhoneSaveError(null);
	if (formData.firstName == '' || 
		formData.lastName == '') {
		return
	}
	if (phoneFormatInvalid) {
		return;
	}
    try {
      const normalizedPhone = formData.phone.trim()
        ? normalizePhoneNumber(formData.phoneCountry, formData.phone) || ''
        : '';

      let profileImageUrl = avatarDoc?.url || user?.profileImage;

      if (formData.profileImage instanceof File) {
        const uploadResponse = await dispatch(
          addUserDocument({
            file: formData.profileImage,
            docType: 'avatar',
          }),
        ).unwrap();
        profileImageUrl = uploadResponse.document.url;
      }

      const profilePayload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        language: formData.profileLanguage,
        middleName: user?.middleName || '',
        phone: normalizedPhone,
        profileImage: profileImageUrl,
      };

      try {
        await dispatch(editProfile(profilePayload)).unwrap();
      } catch (err: any) {
        // editProfile rejects with the response body itself, not an axios error.
        const message = getApiErrorMessage({ response: { data: err } });
        console.error(
          t('profileSettings.toast.profileUpdateFailed'),
          message,
          err,
        );
        if (/phone/i.test(message)) {
          setPhoneSaveError(t('profileSettings.phoneUpdateFailed') as string);
        }
        return;
      }
      console.log(t('profileSettings.toast.profileUpdatedSuccessfully'));

      await Promise.all([
        dispatch(fetchProfile()).unwrap(),
        dispatch(fetchAvatar()).unwrap(),
      ]);
      console.log('Profile and avatar refetched successfully');

      setInitialFormData({
        ...formData,
        profileImage: null, // Reset to null since it's now in avatarDoc
      });
    } catch (err: any) {
      console.error(t('profileSettings.toast.profileUpdateFailed'), err);
    }
  };

  // Expose save handler and form change status via ref
  useEffect(() => {
    if (saveRef) {
      saveRef.current = {
        save: handleSave,
        hasChanges: () => isFormChanged,
      };
    }
  }, [saveRef, isFormChanged, handleSave]);

  return (
    <>
      <Form
        content={
          <form className="flex flex-col gap-4">
            <div className="text-[20px] font-medium">
              {t('profileSettings.title')}
            </div>
            <InputField
              label={t('profileSettings.firstName')}
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
			  error={submitted && !formData.firstName ? t('general.required') as string : undefined}
            />
            <InputField
              label={t('profileSettings.lastName')}
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
			  error={submitted && !formData.lastName ? t('general.required') as string : undefined}
            />
            <InputField
              label={t('profileSettings.email')}
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('profileSettings.email')}
			  disable={true}
            />
            <div className="flex items-start gap-[12px]">
              <div className="w-[45%] shrink-0">
                <Dropdown
                  label={t('profileSettings.phoneCountry')}
                  name="phoneCountry"
                  value={formData.phoneCountry}
                  options={countries.map((country) => ({
                    label: `${t(country.labelKey)} (+${country.dialCode})`,
                    value: country.value,
                  }))}
                  onChange={handleDropdownChange}
                  fullWidth
                />
              </div>
              <div className="flex-1 min-w-0">
                <InputField
                  label={t('profileSettings.phone')}
                  type="tel"
                  name="phone"
                  placeholder={t('profileSettings.phonePlaceholder')}
                  value={formData.phone}
                  onChange={handleChange}
                  error={phoneError}
                  fullWidth
                />
              </div>
            </div>
            {/* <InputField
              label={t('profileSettings.newPassword')}
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder={t('profileSettings.newPassword')}
            />
            <InputField
              label={t('profileSettings.confirmPassword')}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={t('profileSettings.confirmPassword')}
            /> */}
            <Dropdown
              label={t('profileSettings.language')}
              name="profileLanguage"
              value={formData.profileLanguage}
              options={languages.map((lang) => ({
                label: t(lang.labelKey),
                value: lang.value,
              }))}
              onChange={handleDropdownChange}
            />
            <label className="flex flex-col text-[#4F5057] font-medium text-[18px]">
              {t('profileSettings.profilePhoto')}
            </label>
            <label className="flex flex-col text-[#757575] font-medium text-[16px]">
              {t(
                'profileSettings.yourPhotoWillBeVisibleToYourClientsAndCoworkers',
              )}
            </label>
            <div className="flex flex-row gap-[24px]">
              <img
                className="h-[120px] w-[120px] rounded-full object-cover"
                src={
                  formData.profileImage instanceof File
                    ? URL.createObjectURL(formData.profileImage)
                    : avatarDoc?.url || avatar
                }
                alt="Profile"
              />
              <div className="flex flex-col justify-around">
                <button
                  type="button"
                  className="w-[168px] py-[12px] bg-[#E5E5E5] font-bold text-[14px] hover:opacity-75"
                  onClick={handleAddPhotoClick}
                >
                  {avatarDoc || formData.profileImage
                    ? t('profileSettings.changePhoto')
                    : t('profileSettings.addPhoto')}
                </button>
                {(avatarDoc || formData.profileImage) && (
                  <button
                    type="button"
                    className="w-[168px] py-[12px] bg-[#FF6F61] text-white font-bold text-[14px] hover:opacity-75 mt-2"
                    onClick={handleRemovePhoto}
                  >
                    {t('profileSettings.removePhoto')}
                  </button>
                )}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  onChange={handleFileInputChange}
                />
                <label className="text-[12px] text-[#757575] font-medium">
                  {t('profileSettings.fileTypesJpegJpgPng')}
                </label>
              </div>
            </div>
          </form>
        }
        maxWidth={608}
      />
      <StickySaveBar>
        <Button
          onClick={handleSave}
          label={t('profileSettings.saveChanges')}
          loading={loading || docLoading}
          disabled={loading || docLoading || !isFormChanged}
          style={{
            opacity: loading || docLoading || !isFormChanged ? 0.5 : 1,
          }}
        />
      </StickySaveBar>
      <ConfirmationModal
        isOpen={isRemoveModalOpen}
        onClose={() => setIsRemoveModalOpen(false)}
        onConfirm={confirmRemovePhoto}
        loading={loading || docLoading}
        title={t('profileSettings.confirmRemovePhoto')}
        message={t('profileSettings.confirmRemovePhotoMessage')}
      />
    </>
  );
};
